import type { JSX, ReactNode } from 'react';
import { Button } from './Button';
import { EmptyState } from './EmptyState';
import { STATUS_GLYPHS, STATUS_TONES, cx } from './styles';
import type { StatusLevel } from './styles';

/*
 * ============================================================================
 * 通知：为什么必须分成"横幅"和"历史列表"两部分
 * ============================================================================
 *
 * 只用 toast 是不够的。地面站里的通知（"参数写入被拒绝"、"电池电压低于阈值"）
 * 有一个共同点：**用户抬头看屏幕的那一刻，事情已经发生了**。
 * 一闪而过的提示等于没有通知，所以：
 *
 *   `Notification`      内联横幅，常驻在相关区域旁边，直到条件消失（或用户关掉）；
 *   `NotificationList`  可回看的历史列表，带时间与未读标记，**不会自己消失**；
 *   `Toast`             真正瞬时的反馈（"已复制"），看一眼就够，不能占着屏幕。
 *
 * 该用哪一个的判断标准是："如果用户 10 秒后才看屏幕，他还需要知道这件事吗？"
 * 需要 → 横幅或列表；不需要 → toast。
 */

export interface NotificationProps {
  level: StatusLevel;
  title?: ReactNode;
  children?: ReactNode;
  /** 行动区：给用户一条出路，"知道了"以外的选择 */
  actions?: ReactNode;
  onDismiss?: () => void;
  dismissLabel?: string;
  compact?: boolean;
  className?: string;
}

export function Notification({
  level,
  title,
  children,
  actions,
  onDismiss,
  dismissLabel,
  compact = false,
  className,
}: NotificationProps): JSX.Element {
  const tone = STATUS_TONES[level];
  /*
   * 只有"需要立即处理"的等级用 `role="alert"`（会打断读屏器当前正在念的内容）。
   * 把普通提示也设成 alert，结果是读屏器一直在抢话，用户最后干脆不听 —— 那等于
   * 所有通知都失效。其余等级用 `status`（礼貌播报，等用户手上这件事说完）。
   */
  const urgent = level === 'critical' || level === 'warning';

  return (
    <div
      role={urgent ? 'alert' : 'status'}
      className={cx(
        'flex items-start gap-2 rounded-card border',
        compact ? 'p-2' : 'p-3',
        tone.surface,
        tone.border,
        className,
      )}
    >
      {/* 形状与颜色双通道，见 styles.ts 的说明 */}
      <span aria-hidden="true" className={cx('mt-0.5 shrink-0 text-xs', tone.text)}>
        {STATUS_GLYPHS[level]}
      </span>

      <div className="min-w-0 flex-1">
        {title === undefined ? null : (
          <p className={cx('m-0 text-sm font-medium', tone.text)}>{title}</p>
        )}
        {children === undefined ? null : (
          <div className="mt-1 text-xs text-fg-muted">{children}</div>
        )}
        {actions === undefined ? null : (
          <div className="mt-2 flex flex-wrap items-center gap-2">{actions}</div>
        )}
      </div>

      {onDismiss === undefined ? null : (
        <Button
          variant="ghost"
          size="sm"
          onClick={onDismiss}
          aria-label={dismissLabel ?? '关闭通知'}
          className="shrink-0"
        >
          <span aria-hidden="true">✕</span>
        </Button>
      )}
    </div>
  );
}

export interface NotificationItem {
  id: string;
  level: StatusLevel;
  title: ReactNode;
  description?: ReactNode;
  /** 已经格式化好的时间文本（`'14:03:12'`）。组件不做日期本地化 —— 那是应用层的决定 */
  time?: string;
  /** `true` 表示已读；未读会有视觉标记与读屏器可读的"未读" */
  read?: boolean;
  action?: ReactNode;
}

export interface NotificationListProps {
  items: readonly NotificationItem[];
  onDismiss?: (id: string) => void;
  onClear?: () => void;
  clearLabel?: string;
  /** 空列表时说明"为什么是空的"：是"还没发生"，还是"被清掉了" */
  emptyTitle?: string;
  emptyDescription?: string;
  'aria-label'?: string;
  className?: string;
}

/**
 * 通知历史列表：**可以回看**的那一份。
 *
 * 刻意不是 live region：列表里已经存在的内容在挂载时被整段播报是骚扰。
 * 实时播报由 `Toast` 与 `Notification`（role=alert/status）负责，
 * 这份列表负责"我刚才错过了什么"。
 *
 * 排序（最新的在最上面）由调用方决定并保证 —— 组件不猜时间语义。
 */
export function NotificationList({
  items,
  onDismiss,
  onClear,
  clearLabel = '全部清除',
  emptyTitle = '暂无通知',
  emptyDescription,
  'aria-label': ariaLabel = '通知列表',
  className,
}: NotificationListProps): JSX.Element {
  const unreadCount = items.filter((item) => item.read !== true).length;

  return (
    <div
      className={cx('flex min-h-0 flex-col rounded-card border border-line bg-surface-1', className)}
      aria-label={ariaLabel}
      role="group"
    >
      <div className="flex shrink-0 items-center gap-2 border-b border-line px-3 py-2">
        <span className="text-sm font-medium text-fg">通知</span>
        {unreadCount === 0 ? null : (
          <span className="rounded-full border border-accent bg-accent-soft px-1.5 text-[10px] leading-4 text-accent">
            {unreadCount} 条未读
            {/* 徽标数字本身读出来是"3"，补一句完整的表达 */}
            <span className="sr-only">（未读通知数量）</span>
          </span>
        )}
        {onClear === undefined ? null : (
          <Button variant="ghost" size="sm" onClick={onClear} className="ml-auto">
            {clearLabel}
          </Button>
        )}
      </div>

      {items.length === 0 ? (
        <EmptyState compact title={emptyTitle} description={emptyDescription} />
      ) : (
        <ul className="m-0 min-h-0 flex-1 list-none overflow-auto p-0">
          {items.map((item) => {
            const tone = STATUS_TONES[item.level];
            const unread = item.read !== true;

            return (
              <li
                key={item.id}
                data-unread={unread ? 'true' : undefined}
                className={cx(
                  'flex items-start gap-2 border-b border-line px-3 py-2 last:border-b-0',
                  unread && 'bg-accent-soft',
                )}
              >
                <span aria-hidden="true" className={cx('mt-0.5 shrink-0 text-xs', tone.text)}>
                  {STATUS_GLYPHS[item.level]}
                </span>

                <div className="min-w-0 flex-1">
                  <p className="m-0 text-xs font-medium text-fg">
                    {item.title}
                    {/* 未读不能只靠底色：底色在高对比度主题下会消失 */}
                    {unread ? <span className="sr-only">（未读）</span> : null}
                  </p>
                  {item.description === undefined ? null : (
                    <p className="m-0 mt-0.5 text-xs text-fg-muted">{item.description}</p>
                  )}
                  {item.action === undefined ? null : <div className="mt-1">{item.action}</div>}
                </div>

                {item.time === undefined ? null : (
                  <time className="shrink-0 text-[10px] tabular-nums text-fg-subtle">{item.time}</time>
                )}

                {onDismiss === undefined ? null : (
                  <button
                    type="button"
                    aria-label={`删除通知：${typeof item.title === 'string' ? item.title : item.id}`}
                    onClick={() => onDismiss(item.id)}
                    className="shrink-0 rounded-[2px] px-1 text-fg-subtle hover:text-fg"
                  >
                    <span aria-hidden="true">✕</span>
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
